import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import editWhiteIcon from '../../assets/edit-white.svg';
import deleteWhiteIcon from '../../assets/delete-white.svg';
import FiscalYear from "../../components/Filters/FiscalYear";
import InteractionType from "../../components/Interactions/InteractionType";
import AssistanceType from "../../components/Interactions/AssistanceType";
import BulkActionBtn from "../../components/Interactions/BulkActionBtn";
import { InteractionData, CompanyDetails } from "../../services/types";
import { calculateAvailableYears, calculateYearByType, formatDate } from "../../utils/helpers";
import { useAdminContext } from "../../context/AdminContext";
import BackButtonHeader from "../../components/Navigation/BackButtonHeader";
import NoResultsMessage from "../../components/Filters/NoResultsMessage";
import CompanyInteractionTable from "../../components/Companies/CompanyInteractionTable";
import AddInteractionButton from "../../components/Interactions/AddInteractionButton";
import WinsTable from "../../components/Companies/WinsTable";
import { useOrganizationContext } from "../../context/OrganizationContext";
import { LABELS } from "../../utils/constants";

function filterInteractions(
  interactions: InteractionData[],
  year: string,
  interactionType: string,
  assistanceType: string
): InteractionData[] {
  return interactions.filter((interaction) => {
    const matchesYear = year
      ? new Date(interaction.interaction_date).getFullYear().toString() === year
      : true;

    const matchesInteractionType = interactionType
      ? interaction.interaction_type?.id?.toString() === interactionType
      : true;

    const matchesAssistanceType = assistanceType
      ? interaction.assistance_types?.some((a) => a.id.toString() === assistanceType)
      : true;

    return matchesYear && matchesInteractionType && matchesAssistanceType;
  });
};

function CompanyDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isAdmin } = useAdminContext();
    const { partnerId } = useOrganizationContext();

    const [companyData, setCompanyData] = useState<CompanyDetails | null>(null);
    const [interactions, setInteractions] = useState<InteractionData[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [year, setYear] = useState<string>('');
    const [interactionType, setInteractionType] = useState<string>('');
    const [assistanceType, setAssistanceType] = useState<string>('');

    const currFisYear = calculateYearByType(year);
    const availableYears = calculateAvailableYears();
    const companyInitiatives = companyData?.initiatives?.filter(i => i.category === 'project');

    // Flatten outcomes from all initiatives
    const allOutcomes = companyInitiatives?.flatMap(init => init.outcomes || []);

    useEffect(() => {
        const fetchCompanyDetails = async () => {
            try {
                const response = await fetch(`${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getcompanydetails?companyId=${id}`);
                const result = await response.json();

                if (response.ok) {
                    setCompanyData(result.data);
                    console.log('Fetched company details:', result.data);
                    setInteractions(result.data.interactions || []);
                } else {
                    console.error('Failed to fetch company details:', result.message);
                }
            } catch (error) {
                console.error('Error fetching company details:', error);
            }
        };

        fetchCompanyDetails();
    }, [id]);

    const filteredInteractions = filterInteractions(interactions, year, interactionType, assistanceType);


    const handleEditClick = () => {
        navigate(`/companies/edit/${id}`);
    };

    const handleDeleteClick = () => {
        navigate(`/companies/delete/${companyData?.id}`, {
            state: { rowData: companyData }
        });
    };

    const handleRemoveFilters = () => {
        setYear(currFisYear.toString());
        // setSortType('');
        setInteractionType('');
        setAssistanceType('');
    };

    return (
        <>
            <BackButtonHeader navigateBack={() => navigate('/companies')} name="Companies" />
            <div className="p-4 m-2">
                <div className="mb-8 pb-4 border border-solid border-titanium-white rounded-md w-full">
                    <div className='relative flex items-center border-b border-solid border-gotham-gray bg-gotham-gray rounded-t-md mb-4'>
                        <h2 className="text-white text-sm font-semibold mt-3 mb-3 ml-7">{companyData?.legal_name || companyData?.trade_name || 'Unnamed Company'}</h2>
                        <div className="absolute right-5 flex items-center">
                            <button onClick={handleEditClick} className="mr-1 flex items-center">
                                <img className="border border-solid border-gotham-gray h-6 w-6 p-1 rounded-md" src={editWhiteIcon} alt="Edit" />
                            </button>
                            <button onClick={handleDeleteClick} className="flex items-center">
                                <img className="border border-solid border-gotham-gray h-6 w-6 p-1 rounded-md" src={deleteWhiteIcon} alt="Delete" />
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-wrap sm:flex-nowrap ml-3">
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Company UBI:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{companyData?.ubi || 'N/A'}</div>
                        </div>
                    </div>

                    {companyData?.industry?.name &&
                    <div className="flex flex-wrap sm:flex-nowrap ml-3">
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Industry:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{companyData?.industry?.name}</div>
                        </div>
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Sub-Industry:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{companyData?.sub_industry?.name || 'N/A'}</div>
                        </div>
                    </div>
                    }

                    <div className="flex flex-wrap sm:flex-nowrap ml-3">
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Total Interactions:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{companyData?.total_interactions}</div>
                        </div>
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Last Interaction Date:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{formatDate(companyData?.last_interaction_date)}</div>
                        </div>
                    </div>

                    <div className="flex flex-wrap sm:flex-nowrap ml-3">
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Address:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{companyData?.address || "N/A"}</div>
                        </div>
                    </div>

                    {companyData?.latitude && companyData.longitude && 
                    <div className="flex flex-wrap sm:flex-nowrap ml-3">
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Latitude:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{companyData.latitude}</div>
                        </div>
                        <div className="flex flex-wrap items-start sm:items-center mx-4 my-2 w-1/2">
                            <div className="text-gotham-gray text-sm font-semibold">Longitude:</div>
                            <div className="text-gotham-gray text-sm flex items-center w-full">{companyData.longitude}</div>
                        </div>
                    </div>
                    }
                </div>

                <div className="mb-8 border border-solid border-titanium-white rounded-md w-full">
                    <div className="flex flex-wrap items-center my-3 px-2">
                        <h2 className="text-gotham-gray text-sm font-semibold ml-5">{LABELS.ENTITY_NAME} Wins</h2>
                    </div>
                    
                    {allOutcomes && allOutcomes.length > 0 ?
                        <WinsTable id={id} partnerId={String(partnerId)} initiatives={companyInitiatives} from={window.location.pathname}/>
                        :
                        <NoResultsMessage 
                            itemName="outcomes"
                            hasFilters={false}
                            onRemoveFilters={handleRemoveFilters}
                        />
                    }
                </div>

                {!isAdmin && (
                    <div className="mb-8 border border-solid border-titanium-white rounded-md w-full">
                        <div className="flex flex-wrap items-center my-3 px-2">
                            <h2 className="text-gotham-gray text-sm font-semibold ml-5 w-36">Recent Interactions</h2>
                            <div className="ml-auto flex flex-wrap items-center">
                                <FiscalYear filterByYear={setYear} availableYears={availableYears} currYear={year} clearable={true} />
                                <InteractionType filterByInteractionType={setInteractionType} currInteractionType={interactionType} />
                                <AssistanceType filterByAssistanceType={setAssistanceType} currAssistanceType={assistanceType} />
                                <AddInteractionButton id={id} company={companyData} />
                                <BulkActionBtn disabled={selectedRows.length === 0} handleBulkAction={() => console.log("Bulk action:", selectedRows)} selectedRows={selectedRows} hideOption="move" />
                            </div>
                        </div>

                        {filteredInteractions.length > 0 ? (
                            <CompanyInteractionTable data={filteredInteractions} handleSelectRows={setSelectedRows} />
                        ) : (
                            <NoResultsMessage
                                itemName="interactions"
                                hasFilters={true}
                                onRemoveFilters={handleRemoveFilters}
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default CompanyDetail;
